
// Enhanced email validation with better compatibility
export const validateEmail = (email: string): { isValid: boolean; error?: string } => {
  if (!email || typeof email !== 'string') {
    return { isValid: false, error: 'Email is required' };
  }

  const trimmedEmail = email.trim().toLowerCase();
  
  // Check for basic format
  if (!trimmedEmail.includes('@') || !trimmedEmail.includes('.')) {
    return { isValid: false, error: 'Please enter a valid email address' };
  }

  // More flexible email regex that accepts most valid formats
  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  
  if (!emailRegex.test(trimmedEmail)) {
    return { isValid: false, error: 'Please enter a valid email address' };
  }

  // Check for common issues
  if (trimmedEmail.length > 254) {
    return { isValid: false, error: 'Email address is too long' };
  }

  const [localPart, domain] = trimmedEmail.split('@');
  
  if (localPart.length > 64) {
    return { isValid: false, error: 'Email address format is invalid' };
  }

  if (domain.length < 3) {
    return { isValid: false, error: 'Please enter a valid domain name' };
  }

  // Check for consecutive dots
  if (trimmedEmail.includes('..')) {
    return { isValid: false, error: 'Email address format is invalid' };
  }

  // Check for valid domain extension
  if (!domain.includes('.') || domain.endsWith('.') || domain.startsWith('.')) {
    return { isValid: false, error: 'Please enter a valid domain name' };
  }

  return { isValid: true };
};

export const validatePassword = (password: string): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];
  
  if (!password) {
    errors.push('Password is required');
    return { isValid: false, errors };
  }
  
  if (password.length < 6) {
    errors.push('Password must be at least 6 characters long');
  }
  
  // More flexible password requirements
  if (password.length < 8) {
    // For passwords 6-7 characters, require at least one number or special character
    if (!/[\d!@#$%^&*(),.?":{}|<>]/.test(password)) {
      errors.push('Password must contain at least one number or special character');
    }
  } else {
    // For longer passwords, encourage but don't require complexity
    let complexityScore = 0;
    if (/[A-Z]/.test(password)) complexityScore++;
    if (/[a-z]/.test(password)) complexityScore++;
    if (/\d/.test(password)) complexityScore++;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) complexityScore++;
    
    if (complexityScore < 2) {
      errors.push('Password should contain a mix of letters, numbers, or special characters');
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

// Enhanced validation with security measures
export const sanitizeInput = (input: string): string => {
  if (!input || typeof input !== 'string') return '';
  
  return input
    .trim()
    .replace(/[<>]/g, '') // Remove potential XSS characters
    .substring(0, 1000); // Limit length
};

export const validateAmount = (amount: string): boolean => {
  if (!amount) return false;
  const num = parseFloat(amount);
  return !isNaN(num) && num > 0 && num <= 1000000 && /^\d+(\.\d{1,8})?$/.test(amount);
};

export const validateWalletAddress = (address: string): boolean => {
  if (!address) return false;
  // Basic crypto address validation - more flexible
  return /^[a-zA-Z0-9]{20,62}$/.test(address);
};

export const validateReferralCode = (code: string): { isValid: boolean; error?: string } => {
  if (!code || typeof code !== 'string') {
    return { isValid: false, error: 'Referral code is required' };
  }

  const trimmedCode = code.trim().toUpperCase();
  
  if (trimmedCode.length < 6) {
    return { isValid: false, error: 'Referral code must be at least 6 characters' };
  }

  if (trimmedCode.length > 15) {
    return { isValid: false, error: 'Referral code is too long' };
  }

  // Allow alphanumeric characters
  if (!/^[A-Z0-9]+$/.test(trimmedCode)) {
    return { isValid: false, error: 'Referral code can only contain letters and numbers' };
  }

  return { isValid: true };
};

// Rate limiting helper
export const createRateLimiter = (maxRequests: number, windowMs: number) => {
  const requests = new Map();
  
  return (identifier: string): boolean => {
    const now = Date.now();
    const windowStart = now - windowMs;
    
    if (!requests.has(identifier)) {
      requests.set(identifier, []);
    }
    
    const userRequests = requests.get(identifier);
    const validRequests = userRequests.filter((time: number) => time > windowStart);
    
    if (validRequests.length >= maxRequests) {
      return false;
    }
    
    validRequests.push(now);
    requests.set(identifier, validRequests);
    return true;
  };
};

// Email domain validation helper
export const validateEmailDomain = (email: string): { isValid: boolean; suggestion?: string } => {
  const commonDomains = [
    'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'icloud.com',
    'aol.com', 'live.com', 'msn.com', 'ymail.com', 'protonmail.com'
  ];

  const domain = email.split('@')[1]?.toLowerCase();
  if (!domain) return { isValid: false };

  // Check for common typos
  const typoMap: { [key: string]: string } = {
    'gmai.com': 'gmail.com',
    'gmial.com': 'gmail.com',
    'gmail.co': 'gmail.com',
    'yahooo.com': 'yahoo.com',
    'yaho.com': 'yahoo.com',
    'hotmial.com': 'hotmail.com',
    'hotmai.com': 'hotmail.com',
    'outlok.com': 'outlook.com',
    'outloo.com': 'outlook.com'
  };

  if (typoMap[domain]) {
    return { 
      isValid: false, 
      suggestion: email.replace(domain, typoMap[domain])
    };
  }

  return { isValid: true };
};
